import { Box, Modal, Text, TextInput, Textarea } from '@tocktix/pantry';
import { DeleteUserStatus } from 'tools/bff/generated/user/user_administration_pb';
import { FunctionComponent, useState } from 'react';
import { useActions } from 'utils/connect';
import { useForm } from 'react-hook-form';
import { userClient } from 'tools/bff/client';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';

interface DeleteUserModalProps {
  uuid: string;
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const formSchema = z.object({
  reason: z.string().trim().nonempty('Reason is required'),
  confirmation: z.string().refine((value) => value === 'DELETE', 'Type DELETE to confirm'),
});

type FormFields = z.infer<typeof formSchema>;

const DeleteUserModal: FunctionComponent<DeleteUserModalProps> = ({
  uuid,
  open,
  onClose,
  onSubmit,
}) => {
  const [error, setError] = useState<string | null>(null);
  const { actions } = useActions();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
  });

  const handleClose = () => {
    setError(null);
    reset();
    onClose();
  };

  const handleSubmission = async (data: FormFields) => {
    setError(null);
    try {
      const response = await userClient.deleteUsers({
        uuids: [uuid],
        reason: data.reason.trim(),
      });
      const result = response.results[0];
      switch (result?.status) {
        case DeleteUserStatus.DELETED:
          reset();
          onSubmit();
          actions.toast.showInfo('Account deleted');
          return;
        case DeleteUserStatus.NOT_FOUND:
          setError('User not found.');
          actions.toast.showError('User not found.');
          return;
        default:
          setError('Error deleting user' + (result?.error ? ': ' + result.error : '.'));
          actions.toast.showError('Error deleting user');
          return;
      }
    } catch (reason) {
      setError('Error deleting user: ' + reason);
      actions.toast.showError('Error deleting user');
    }
  };

  return (
    <form onSubmit={handleSubmit(handleSubmission)}>
      <Modal
        id="delete-user-modal"
        open={open}
        onClose={handleClose}
        size="md"
        data-testid="delete-user-modal"
      >
        <Modal.Header title="Delete account" onClose={handleClose} border />
        <Modal.Body>
          <Box display="flex" flexDirection="column" gap={200}>
            <Text variant="body" color="fgDangerBase">
              This permanently deletes the account across Resy, Tock, and GDUS. This action cannot
              be undone.
            </Text>
            <Textarea
              id="delete-user-reason"
              label="Reason"
              required
              fullWidth
              data-testid="delete-user-reason-input"
              validationMessage={errors.reason?.message}
              validationStatus="error"
              {...register('reason')}
            />
            <TextInput
              id="delete-user-confirmation"
              label='Type "DELETE" to confirm'
              required
              fullWidth
              data-testid="delete-user-confirmation-input"
              validationMessage={errors.confirmation?.message}
              validationStatus="error"
              {...register('confirmation')}
            />
            {error && (
              <Text variant="body" color="fgDangerBase">
                {error}
              </Text>
            )}
          </Box>
        </Modal.Body>
        <Modal.Footer
          actions={{
            primary: {
              label: isSubmitting ? 'Deleting...' : 'Delete',
              danger: true,
              type: 'submit',
              disabled: !isValid || isSubmitting,
              onClick: () => {
                handleSubmit(handleSubmission)();
              },
            },
            cancel: {
              label: 'Cancel',
              onClick: handleClose,
            },
          }}
        />
      </Modal>
    </form>
  );
};

export default DeleteUserModal;
